// Lazy ArUco marker detection (js-aruco2). Pure JS (no wasm), so once the
// module is imported detection runs synchronously per frame. Lazy-imported so
// it only loads when a marker rule is active.
//
// js-aruco2 bundles two dictionaries. The detector MUST use the same dictionary
// the printed markers were generated with, otherwise the id is read wrong (the
// closest code within the Hamming tolerance is returned) — that's the cause of
// "marker 1 detected as 159". The user picks which dictionary they printed.

// Selectable dictionaries (only these two are bundled in js-aruco2).
export const MARKER_DICTS = [
    { key: 'ARUCO_MIP_36h12', label: 'MIP 36h12 (6×6)' },
    { key: 'ARUCO', label: 'Original (5×5)' },
]
export const DEFAULT_MARKER_DICT = 'ARUCO_MIP_36h12'

const DICT_KEY = 'aiMarkerDict'
export const loadMarkerDict = () =>
    sessionStorage.getItem(DICT_KEY) || DEFAULT_MARKER_DICT
export const saveMarkerDict = (d) => sessionStorage.setItem(DICT_KEY, d)

// One detector per dictionary, created on first use.
const detectorPromises = {}

async function getDetector(dict) {
    const name = dict || DEFAULT_MARKER_DICT
    if (!detectorPromises[name]) {
        detectorPromises[name] = import('js-aruco2')
            .then((m) => {
                const AR = m.AR || (m.default && m.default.AR) || m.default
                return new AR.Detector({ dictionaryName: name })
            })
            .catch((e) => {
                detectorPromises[name] = null
                throw e
            })
    }
    return detectorPromises[name]
}

// Detect markers in an ImageData (RGBA). Returns an array of numeric marker ids.
export async function detectMarkerIds(imageData, dict) {
    if (!imageData) return []
    try {
        const detector = await getDetector(dict || loadMarkerDict())
        const markers = detector.detectImage(
            imageData.width,
            imageData.height,
            imageData.data
        )
        return (markers || []).map((mk) => mk.id)
    } catch (e) {
        return []
    }
}

// Full detection: [{ id, corners:[{x,y}...] }] in pixel coords (for overlays).
export async function detectMarkers(imageData, dict) {
    if (!imageData) return []
    try {
        const detector = await getDetector(dict || loadMarkerDict())
        const markers = detector.detectImage(
            imageData.width,
            imageData.height,
            imageData.data
        )
        return (markers || []).map((mk) => ({ id: mk.id, corners: mk.corners }))
    } catch (e) {
        return []
    }
}
